import { QRCodeSVG } from 'qrcode.react';
import {
  IconArrowRight,
  IconClock,
  IconColumns,
  IconList,
  IconMoped,
  IconSettings,
  IconStore,
  IconUtensils,
  IconWhatsapp,
} from '../atoms/Icon';

/* ═══════════════════════════════════════════════════════════════════════════
   As telas do produto, desenhadas em CSS.

   Juntas num arquivo porque são a mesma coisa: ilustração. Nenhuma tem estado,
   nenhuma recebe dado de fora, e separá-las em vários arquivos de dez linhas
   espalharia a decisão de "como o produto aparece na página" por uma pasta
   inteira.

   Por que não capturas de tela: o produto muda toda semana e uma imagem
   envelhece na página sem ninguém notar. Desenhadas assim elas seguem os
   tokens, ficam nítidas em qualquer densidade de tela e não pesam no
   carregamento.

   Todas saem com `aria-hidden`: são decoração do argumento que está escrito ao
   lado. Um leitor de tela lendo "Pizza Grande R$ 62,00 Confirmação de pagamento
   3" no meio de um parágrafo atrapalharia em vez de informar.
   ═══════════════════════════════════════════════════════════════════════════ */

export function BrowserFrame({ url, children, className = '' }) {
  return (
    <div className={`mock mock-browser ${className}`} aria-hidden="true">
      <div className="mock-browser-bar">
        <span className="mock-dot" />
        <span className="mock-dot" />
        <span className="mock-dot" />
        <span className="mock-url">{url}</span>
      </div>
      {children}
    </div>
  );
}

export function PhoneFrame({ children, className = '' }) {
  return (
    <div className={`mock mock-phone ${className}`} aria-hidden="true">
      <div className="mock-phone-screen">
        <div className="mock-phone-notch" />
        {children}
      </div>
    </div>
  );
}

const PRODUTOS = [
  { emoji: '🍕', nome: 'Pizza Grande', apoio: 'Escolha até 2 sabores', preco: 'R$ 62,00' },
  { emoji: '🍔', nome: 'Burger da casa', apoio: 'Blend 180g, cheddar e bacon', preco: 'R$ 34,00' },
  { emoji: '🥤', nome: 'Coca-Cola lata', apoio: '350ml gelada', preco: 'R$ 7,00' },
];

/** O cardápio do cliente: cabeçalho com a marca do estabelecimento, trilho de
 *  categorias, produtos e a barra do carrinho. */
export function MenuMockup() {
  return (
    <>
      <div className="mock-menu-head">
        <span className="mock-avatar">🍕</span>
        <div>
          <b>Pizzaria do Zé</b>
          <span>Aberto agora · Entrega e retirada</span>
        </div>
      </div>

      <div className="mock-chips">
        <span className="mock-chip is-active">Pizzas</span>
        <span className="mock-chip">Lanches</span>
        <span className="mock-chip">Bebidas</span>
        <span className="mock-chip">Sobremesas</span>
      </div>

      {PRODUTOS.map((p) => (
        <div className="mock-product" key={p.nome}>
          <span className="mock-thumb">{p.emoji}</span>
          <div className="mock-product-info">
            <b>{p.nome}</b>
            <span>{p.apoio}</span>
          </div>
          <span className="mock-price">{p.preco}</span>
        </div>
      ))}

      <div className="mock-cartbar">
        <span>3 itens · R$ 84,50</span>
        <span className="d-inline-flex align-items-center gap-1">
          <IconWhatsapp size={13} /> Enviar
        </span>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Kanban do painel.

   Reproduz o que a captura de tela real do admin mostra: barra lateral,
   cabeçalho com o estabelecimento logado e cartões com avatar do cliente,
   miniatura do item e duas ações no rodapé. Uma versão simplificada demais
   (só número, hora e preço) tinha ficado parecida DEMAIS com um placeholder
   genérico de dashboard, e não com o produto.
   ═══════════════════════════════════════════════════════════════════════════ */

const NAV = [
  { Icone: IconColumns, ativo: true },
  { Icone: IconList, ativo: false },
  { Icone: IconUtensils, ativo: false },
  { Icone: IconSettings, ativo: false },
];

/* Cor do avatar do cliente por posição, não por hash do nome: é ilustração, e
   um ciclo fixo de três tons já basta para ler como "cada cliente tem a sua
   cor", que é o ponto — sem depender de sorte com o hash de um nome de
   exemplo. */
const TONS_AVATAR = ['var(--color-purple-primary)', 'var(--color-amber-primary)', '#2a9d5c'];

const COLUNAS = [
  {
    id: 'pagamento',
    rotulo: 'Pagamento',
    tom: 'amber',
    cor: 'var(--color-amber-primary)',
    avancar: null,
    pedidos: [
      {
        numero: '#1028',
        hora: '19:42',
        cliente: 'Marina Souza',
        modo: 'Retirada',
        Icone: IconStore,
        item: { emoji: '🍕', nome: 'Pizza Grande', qtd: 1 },
        total: 'R$ 84,50',
      },
    ],
  },
  {
    id: 'preparando',
    rotulo: 'Preparando',
    tom: 'purple',
    cor: 'var(--color-purple-primary)',
    avancar: { rotulo: 'Pronto', cor: '#2a9d5c' },
    pedidos: [
      {
        numero: '#1027',
        hora: '19:35',
        cliente: 'Rafael Lima',
        modo: 'Entrega',
        Icone: IconMoped,
        item: { emoji: '🍔', nome: 'Burger da casa', qtd: 2 },
        total: 'R$ 68,00',
      },
      {
        numero: '#1026',
        hora: '19:28',
        cliente: 'Mesa 4',
        modo: 'No local',
        Icone: IconUtensils,
        item: { emoji: '🍕', nome: 'Pizza Grande', qtd: 1 },
        total: 'R$ 96,00',
      },
    ],
  },
  {
    id: 'pronto',
    rotulo: 'Pronto',
    tom: 'green',
    cor: '#2a9d5c',
    avancar: null,
    pedidos: [
      {
        numero: '#1025',
        hora: '19:11',
        cliente: 'Ana Paula',
        modo: 'Retirada',
        Icone: IconStore,
        item: { emoji: '🥤', nome: 'Coca-Cola lata', qtd: 2 },
        total: 'R$ 38,00',
      },
    ],
  },
];

function OrderMock({ pedido, corColuna, avancar }) {
  return (
    <div className="mock-order">
      <div className="mock-order-top">
        <span className="mock-order-badge" style={{ color: corColuna, backgroundColor: `${corColuna}1f` }}>
          {pedido.numero}
        </span>
        <span className="mock-order-time">{pedido.hora}</span>
      </div>

      <div className="mock-order-modo">
        <pedido.Icone size={12} />
        {pedido.modo}
      </div>

      <div className="mock-order-cliente">
        <span
          className="mock-order-avatar"
          style={{ backgroundColor: TONS_AVATAR[pedido.numero.length % TONS_AVATAR.length] }}
        >
          {pedido.cliente[0]}
        </span>
        {pedido.cliente}
      </div>

      <div className="mock-order-item">
        <span className="mock-thumb mock-thumb--sm">{pedido.item.emoji}</span>
        <span className="text-truncate">
          {pedido.item.qtd}× {pedido.item.nome}
        </span>
      </div>

      <div className="mock-order-foot">
        <span className="mock-order-total">{pedido.total}</span>
        <span className="mock-order-actions">
          <span className="mock-order-btn mock-order-btn--whats">
            <IconWhatsapp size={11} />
          </span>
          {avancar ? (
            <span className="mock-order-btn mock-order-btn--avancar" style={{ backgroundColor: avancar.cor }}>
              {avancar.rotulo} <IconArrowRight size={10} />
            </span>
          ) : (
            <span className="mock-order-btn mock-order-btn--ghost">Detalhes</span>
          )}
        </span>
      </div>
    </div>
  );
}

/** O quadro completo, com a moldura do próprio produto ao redor: barra lateral
 *  com o logo e o cabeçalho com o estabelecimento logado. Sem esses dois, o
 *  quadro de cartões coloridos podia ser tela de qualquer sistema de tarefas;
 *  com eles, é claramente ESTA tela do MyPedidos. */
export function KanbanMockup() {
  return (
    <div className="mock-app">
      <div className="mock-sidebar">
        <span className="mock-sidebar-logo">M</span>
        {NAV.map(({ Icone, ativo }, i) => (
          <span className={`mock-sidebar-item${ativo ? ' is-active' : ''}`} key={i}>
            <Icone size={15} />
          </span>
        ))}
      </div>

      <div className="mock-app-main">
        <div className="mock-topbar">
          <span className="mock-topbar-avatar">🍕</span>
          <div className="mock-topbar-info">
            <span className="mock-topbar-name">
              Pizzaria do Zé <span className="mock-badge-plan">FREEMIUM</span>
            </span>
          </div>
        </div>

        <div className="mock-kanban">
          {COLUNAS.map((col) => (
            <div className={`mock-col mock-col--${col.tom}`} key={col.id}>
              <div className="mock-col-head">
                <span className="mock-col-dot" style={{ backgroundColor: col.cor }} />
                <span className="text-truncate">{col.rotulo}</span>
                <span className="mock-col-count" style={{ color: col.cor }}>
                  {col.pedidos.length}
                </span>
              </div>

              {col.pedidos.map((p) => (
                <OrderMock pedido={p} corColuna={col.cor} avancar={col.avancar} key={p.numero} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** O asterisco do WhatsApp vira negrito de verdade. Sem isto a bolha mostraria
 *  os asteriscos crus, que é justamente o que a conversa NÃO mostra. */
function comNegrito(texto) {
  return texto.split(/\*(.+?)\*/g).map((parte, i) =>
    i % 2 === 1 ? <b key={i}>{parte}</b> : parte,
  );
}

/** A mensagem que o cliente manda ao fechar o pedido. O logo do estabelecimento
 *  entra na PRÓPRIA mensagem, como foto de perfil de quem está na conversa —
 *  não numa segunda bolha separada, que é onde uma versão anterior desta
 *  maquete errava. */
export function ChatMockup({ mensagem }) {
  return (
    <div className="mock mock-chat" aria-hidden="true">
      <div className="mock-chat-head">
        <span className="mock-chat-avatar">🍕</span>
        <div>
          <b>Pizzaria do Zé</b>
          <span>online</span>
        </div>
      </div>
      <div className="mock-chat-body">
        <div className="mock-bubble">
          {comNegrito(mensagem)}
          <span className="mock-bubble-time">19:42 ✓✓</span>
        </div>
      </div>
    </div>
  );
}

/** A tela que o link do pedido abre, reproduzida de `OrderStatusPage.js`: o
 *  cabeçalho leva o logo e o nome do estabelecimento, na cor que ELE escolheu
 *  (aqui, o roxo de exemplo) — não uma cor fixa do MyPedidos. Abaixo, a etapa
 *  atual, os três marcos do pedido e, para quem ainda não pagou, o mesmo
 *  cartão de Pix de `PixPagamento.js`. É a prova visual de que o
 *  acompanhamento não vive só dentro da conversa do WhatsApp. */
export function TrackingMockup({ dados }) {
  return (
    <PhoneFrame className="mock-track">
      <div className="mock-track-head">
        <span className="mock-chat-avatar mock-chat-avatar--sm">🍕</span>
        <div>
          <b>Pedido {dados.pedido}</b>
          <span>
            {dados.modalidade} · {dados.estabelecimento}
          </span>
        </div>
      </div>

      <div className="mock-track-body">
        <span className="mock-track-icon">
          <IconClock size={18} />
        </span>
        <div className="mock-track-titulo">{dados.etapaTitulo}</div>
        <p className="mock-track-texto">{dados.etapaTexto}</p>

        <div className="mock-track-steps">
          {dados.etapas.map((etapa, i) => (
            <div className={`mock-track-step${i === dados.etapaAtiva ? ' is-active' : ''}`} key={etapa}>
              <span className="mock-track-step-dot" />
              <span>{etapa}</span>
            </div>
          ))}
        </div>

        <div className="mock-track-whats">
          <IconWhatsapp size={13} />
          {dados.botaoWhatsapp}
        </div>

        <div className="mock-track-pix">
          <div className="mock-pix-titulo">Pague com Pix</div>
          <div className="mock-qr mock-qr--sm">
            <QRCodeSVG value="https://mypedidos.com.br" size={72} level="M" marginSize={0} fgColor="#2A2A33" />
          </div>
          <div className="mock-track-copia">00020126...pix0126dr.pedr</div>
        </div>
      </div>
    </PhoneFrame>
  );
}

/** O bloco de Pix do cardápio: QR real (gerado com o mesmo `qrcode.react` do
 *  cardápio, em `PixPagamento.js`), chave e beneficiário. O valor codificado é
 *  a URL da própria página, não um payload de pagamento: aponta para algo real
 *  em vez de fingir um código Pix que ninguém deveria escanear. */
export function PixMockup() {
  return (
    <div className="mock mock-pix" aria-hidden="true">
      <div className="mock-qr">
        <QRCodeSVG value="https://mypedidos.com.br" size={92} level="M" marginSize={0} fgColor="#2A2A33" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="mock-pix-titulo">Pague com Pix</div>
        <div className="mock-pix-linha">Chave (CNPJ): 12.345.678/0001-90</div>
        <div className="mock-pix-linha">Beneficiário: Pizzaria do Zé</div>
        <div className="mock-pix-copiar">
          Copiar código <IconArrowRight size={12} />
        </div>
      </div>
    </div>
  );
}
